/*
 * The error `services/http` throws for a non-2xx answer, on its own so that code which only needs
 * to recognise one (`instanceof HttpError`) can import it without the browser session module —
 * `http.ts` reads `localStorage` and `window.location` while loading, which is exactly what a
 * headless Hubby run has none of (HUBBY-HEADLESS-ACTIONS-PLAN.md S3a). `http.ts` re-exports it, so
 * every existing import is unaffected; a host that supplies its own transport throws this same
 * class so the shared API modules' checks keep working.
 */

/** Reason phrases for the statuses the API and the edge in front of it actually answer with. */
const REASON_PHRASES: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    405: 'Method Not Allowed',
    408: 'Request Timeout',
    409: 'Conflict',
    413: 'Payload Too Large',
    415: 'Unsupported Media Type',
    422: 'Unprocessable Entity',
    429: 'Too Many Requests',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout'
};

/**
 * `HTTP 403 Forbidden` — what a failure is called when the response said nothing else.
 *
 * `statusText` is used only when the table above has no phrase: over HTTP/2 it is always empty,
 * so it cannot be the primary source (see `HttpError`).
 */
export function httpStatusLine(status: number, statusText = ''): string {
    const reason = REASON_PHRASES[status] ?? statusText.trim();
    return reason ? `HTTP ${status} ${reason}` : `HTTP ${status}`;
}

/**
 * The API's own words for a failure, from an error body: `title`, else `detail`, else `message`.
 * Empty when the body has none of them (an empty body, HTML from the edge, `{ code, status }`).
 */
export function httpErrorBodyMessage(body: unknown): string {
    if (body === null || typeof body !== 'object' || Array.isArray(body)) {
        return '';
    }
    const record = body as Record<string, unknown>;
    for (const key of ['title', 'detail', 'message']) {
        const value = record[key];
        if (typeof value === 'string' && value.trim()) {
            return value;
        }
    }
    return '';
}

export class HttpError extends Error {
    readonly status: number;
    readonly statusText: string;
    /**
     * True when the response carried no text of its own and `message` is the status line.
     *
     * ★ `message` IS NEVER EMPTY (ROBO-593). It used to be `title ?? response.statusText`, and over
     *   HTTP/2 `statusText` is always empty — so any non-2xx whose body had no string `title` (an
     *   authorization refusal answers 403 with no body at all) became an error with the message
     *   "". Hubby's `list_cart_fees` relayed that as `{"ok":false,"error":""}` and the model
     *   dropped fees from a pre-launch audit without a word.
     *
     *   The status line is for logs and for the model. A page that has its own sentence for the
     *   failure ("Could not load the product fees") should keep showing it: read the message
     *   through `apiErrorText`, which answers '' for a status-only error.
     */
    readonly isStatusOnly: boolean;

    constructor(message: string, status: number, statusText: string, cause?: unknown) {
        const given = typeof message === 'string' ? message.trim() : '';
        super(given ? message : httpStatusLine(status, statusText), { cause });
        this.name = 'HttpError';
        this.status = status;
        this.statusText = statusText;
        this.isStatusOnly = !given;
    }
}

/**
 * What the server said about a failure, or '' when it said nothing — for `apiErrorText(error) ||
 * 'your own sentence'`. Unlike `error.message`, a status-only `HttpError` yields '', so the
 * caller's wording wins over "HTTP 500 Internal Server Error".
 */
export function apiErrorText(error: unknown): string {
    if (error instanceof HttpError) {
        return error.isStatusOnly ? '' : error.message;
    }
    return error instanceof Error ? error.message : '';
}
